"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { useApp } from "./AppProvider";
import type { NavbarData, SiteSettings } from "@/lib/cms";

// ─── SVG Logo (fallback when no image uploaded) ────────────────────────────────
export function QumrahLogo({ size = 44 }: { size?: number }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      <defs>
        <linearGradient id="lg1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E3017D" />
          <stop offset="100%" stopColor="#71358C" />
        </linearGradient>
        <linearGradient id="lg2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#86C8AE" />
          <stop offset="100%" stopColor="#07AEB9" />
        </linearGradient>
      </defs>
      <path d="M20 15 Q15 15 15 20 L15 75 Q15 80 20 80 L75 80 Q80 80 80 75 L80 45 L70 45 L70 70 L25 70 L25 25 L70 25 L70 35 L80 35 L80 20 Q80 15 75 15 Z" fill="url(#lg1)" />
      <path d="M35 62 L50 42 L65 62 L72 55 L50 28 L28 55 Z" fill="url(#lg2)" />
    </svg>
  );
}

interface NavbarProps {
  data: NavbarData;
  site: SiteSettings;
}

export default function Navbar({ data, site }: NavbarProps) {
  const { theme, lang, toggleTheme, isRTL } = useApp();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [themeAnimating, setThemeAnimating] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const getLocalizedPath = (newLang: string) => {
    if (!pathname) return `/${newLang}`;
    const segments = pathname.split('/');
    if (segments[1] === 'en' || segments[1] === 'ar') {
      segments[1] = newLang;
      return segments.join('/') || '/';
    }
    return `/${newLang}${pathname}`;
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleThemeToggle = () => {
    setThemeAnimating(true);
    toggleTheme();
    setTimeout(() => setThemeAnimating(false), 500);
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <header style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
      {/* Top Bar for Contact */}
      <div
        className="topbar-contacts"
        style={{
          background: "var(--gradient-brand)", color: "white", padding: scrolled ? "0" : "0.35rem 0",
          fontSize: "0.8rem", letterSpacing: "0.05em",
          transition: "all 0.4s ease", maxHeight: scrolled ? 0 : 40, opacity: scrolled ? 0 : 1, overflow: "hidden"
        }}
      >
        <div className="container" style={{ display: "flex", justifyContent: "flex-end", gap: "2rem", alignItems: "center", flexDirection: isRTL ? "row-reverse" : "row" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>✉️ {site.primaryEmail}</span>
          <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>📞 <span dir="ltr">{site.primaryPhone}</span></span>
        </div>
      </div>

      <nav style={{
        padding: scrolled ? "0.8rem 0" : "1.2rem 0", transition: "all 0.4s ease",
        background: scrolled ? "var(--nav-scrolled-bg)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? "1px solid var(--nav-border)" : "none",
      }}>
        <div className="container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>

          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexShrink: 0, zIndex: 1001 }}>
            {site.logo?.url ? (
              <img src={site.logo.url} alt={site.logo.alt ?? site.siteName} style={{ height: 44, width: "auto" }} />
            ) : (
              <QumrahLogo size={44} />
            )}
            <div>
              <div style={{ fontSize: "1.3rem", fontWeight: 900, color: "var(--text-primary)", letterSpacing: "0.08em", lineHeight: 1, transition: "color 0.4s ease" }}>
                {site.siteName}
              </div>
              <div style={{ fontSize: "0.7rem", color: "var(--color-medium-gray)", letterSpacing: "0.18em", fontWeight: 500, textTransform: "uppercase" }}>
                {site.tagline}
              </div>
            </div>
          </div>

          {/* Desktop Nav Links */}
          <div className="nav-desktop-links">
            {data.links.map((link) => (
              <a key={link.href + link.label_en} href={link.href} className="nav-link">
                {isRTL ? (link.label_ar || link.label_en) : link.label_en}
              </a>
            ))}
          </div>

          {/* Desktop Controls: Lang + Theme + CTA */}
          <div className="nav-desktop-controls">
            {/* Language Toggle */}
            <div className="lang-toggle">
              <Link href={getLocalizedPath("en")} className={`lang-btn ${lang === "en" ? "active" : ""}`} style={{ textDecoration: 'none' }}>EN</Link>
              <Link href={getLocalizedPath("ar")} className={`lang-btn ${lang === "ar" ? "active" : ""}`} style={{ textDecoration: 'none' }}>عربي</Link>
            </div>

            {/* Theme Toggle */}
            <button
              className={`theme-toggle ${themeAnimating ? "animate-theme-switch" : ""}`}
              onClick={handleThemeToggle}
              title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {theme === "dark" ? "☀️" : "🌙"}
            </button>

            {/* CTA */}
            <a href={data.cta_href} className="btn-primary" style={{ padding: "0.6rem 1.5rem", fontSize: "0.85rem", textDecoration: "none" }}>
              {isRTL ? (data.cta_ar || data.cta_en) : data.cta_en}
            </a>
          </div>

          {/* Hamburger — Mobile only */}
          <button
            className={`hamburger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            style={{ zIndex: 1001 }}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu ${menuOpen ? "open" : ""}`} style={{ paddingTop: "80px" }}>
        {/* Nav Links */}
        {data.links.map((link) => (
          <a
            key={link.href + link.label_en}
            href={link.href}
            className="nav-link"
            style={{ fontSize: "1.3rem", fontWeight: 600 }}
            onClick={closeMenu}
          >
            {isRTL ? (link.label_ar || link.label_en) : link.label_en}
          </a>
        ))}

        {/* Divider */}
        <div style={{ width: "60%", height: 1, background: "var(--border-subtle)", margin: "0.5rem 0" }} />

        {/* Language Toggle */}
        <div className="lang-toggle" style={{ transform: "scale(1.2)" }}>
          <Link href={getLocalizedPath("en")} className={`lang-btn ${lang === "en" ? "active" : ""}`} onClick={closeMenu} style={{ textDecoration: 'none' }}>EN</Link>
          <Link href={getLocalizedPath("ar")} className={`lang-btn ${lang === "ar" ? "active" : ""}`} onClick={closeMenu} style={{ textDecoration: 'none' }}>عربي</Link>
        </div>

        {/* Theme Toggle */}
        <button
          className={`theme-toggle ${themeAnimating ? "animate-theme-switch" : ""}`}
          onClick={handleThemeToggle}
          title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
          style={{ width: 52, height: 52, fontSize: "1.3rem" }}
        >
          {theme === "dark" ? "☀️" : "🌙"}
        </button>

        {/* CTA */}
        <a
          href={data.cta_href}
          className="btn-primary"
          style={{ padding: "0.85rem 2.5rem", fontSize: "1rem", textDecoration: "none", marginTop: "0.5rem" }}
          onClick={closeMenu}
        >
          {isRTL ? (data.cta_ar || data.cta_en) : data.cta_en}
        </a>
      </div>
    </header>
  );
}
